import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { hasRole, hasAnyRole } from './utils/roles';
import Layout from './components/common/Layout';
import DashboardLayout from './components/dashboard/DashboardLayout';
import ModernDashboard from './components/dashboard/ModernDashboard';
import InsuranceManagement from './pages/InsuranceManagement';
import PatientList from './components/patients/PatientList';
import PatientDetail from './components/patients/PatientDetail';
import StockModule from './components/stock/StockModule';
import FacturationModule from './components/facturation/FacturationModule';
import ConsultationList from './components/consultations/ConsultationList';
import AdminModule from './components/admin';
import Login from './components/auth/Login';
import './App.css';
import './index.css';
import PlanningDashboard from './components/planning/PlanningDashboard';
import CommunicationCenter from './components/communications/CommunicationCenter';
import LogsDashboard from './components/logs/LogsDashboard';
import NotificationCenter from './components/notifications/NotificationCenter';
import { ProsthesisModule } from './components/prosthesis';
import { DepenseModule } from './components/depenses';
import { CaisseModule } from './components/caisse';
import { TresorerieModule } from './components/tresorerie';
import { ComptabiliteModule } from './components/comptabilite';
import DisponibilitesPage from './components/appointments/DisponibilitesPage';
import WhatsAppDashboard from './components/appointments/WhatsAppDashboard';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !hasRole(user, 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès refusé</h1>
          <p className="text-gray-600 mb-4">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  if (allowedRoles && !hasAnyRole(user, allowedRoles as any)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès refusé</h1>
          <p className="text-gray-600 mb-4">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'comptable']}>
            <ModernDashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/legacy" element={
          <ProtectedRoute requireAdmin={true}>
            <DashboardLayout />
          </ProtectedRoute>
        } />
        <Route path="/insurance-management" element={<InsuranceManagement />} />
        <Route path="/monthly-invoices" element={<InsuranceManagement />} />
        <Route path="/patients" element={<PatientList />} />
        <Route path="/patients/:id" element={<PatientDetail />} />
        <Route path="/planning" element={<PlanningDashboard />} />
        <Route path="/consultations" element={<ConsultationList />} />
        <Route path="/facturation" element={<FacturationModule />} />
        <Route path="/caisse" element={
          <ProtectedRoute allowedRoles={['admin', 'secretary']}>
            <CaisseModule />
          </ProtectedRoute>
        } />
        <Route path="/tresorerie" element={
          <ProtectedRoute allowedRoles={['admin', 'secretary', 'comptable']}>
            <TresorerieModule />
          </ProtectedRoute>
        } />
        <Route path="/comptabilite" element={
          <ProtectedRoute allowedRoles={['admin', 'comptable']}>
            <ComptabiliteModule />
          </ProtectedRoute>
        } />
        <Route path="/stock" element={<StockModule />} />
        <Route path="/communications" element={<CommunicationCenter />} />
        <Route path="/notifications" element={<NotificationCenter />} />
        <Route path="/prosthesis" element={<ProsthesisModule />} />
        <Route path="/depenses" element={<DepenseModule />} />
        <Route path="/appointments/disponibilites" element={
          <ProtectedRoute allowedRoles={['admin', 'secretary']}>
            <DisponibilitesPage />
          </ProtectedRoute>
        } />
        <Route path="/appointments/whatsapp" element={
          <ProtectedRoute allowedRoles={['admin', 'secretary']}>
            <WhatsAppDashboard />
          </ProtectedRoute>
        } />
        <Route path="/administration" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminModule />
          </ProtectedRoute>
        } />
        <Route path="/logs" element={
          <ProtectedRoute requireAdmin={true}>
            <LogsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/planning" />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;